/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useAuthAccountId, useAuthUserClaims, useTheme } from '@nbx/frontend-helpers/subscribe';
import {
  publishErrorNotification,
  publishTheme,
  publishAuthAccountIdChanged
} from '@nbx/frontend-helpers/publish';
import { Context } from 'state/store';
import {
  setOverlay,
  setActiveAccount,
  setUserIsAdmin,
  switchAccount,
  doneSwitchingAccount
} from 'state/creators';
import { useIsMobile } from 'helpers/hooks';
import { isNullish } from 'helpers/objects';
import ERRORS from 'constants/errors';
import OVERLAYS from 'constants/overlays';

import StickyHeader from './StickyHeader';
import HorizontalNavigation from '../HorizontalNavigation';
import HeaderMenuDesktop from '../HeaderMenuDesktop';
import HeaderMenuMobile from '../HeaderMenuMobile';

import styles from './styles.module.scss';

export function Header(props) {
  const [state, dispatch] = React.useContext(Context);
  const accountId = useAuthAccountId();
  const claims = useAuthUserClaims();
  const isMobile = useIsMobile();
  const theme = useTheme();

  // Keep context accountID in sync
  React.useEffect(() => {
    dispatch(setActiveAccount(accountId));
  }, [dispatch, accountId]);

  // Keep context isAdmin claim in sync
  React.useEffect(() => {
    const isAdmin = claims?.roles ? claims.roles.includes('admin') : false;
    dispatch(setUserIsAdmin(isAdmin));
  }, [dispatch, claims]);

  // Functions needed for mobile and desktop menus
  // Logic for switching accounts
  const doSwitchAccount = async (oldAccountId, newAccountId) => {
    if (oldAccountId === newAccountId) {
      toggleMenu();
      return;
    }
    dispatch(switchAccount(newAccountId));
    try {
      // Attempt to switch accounts, getting a token and claims info
      const session = await window.GET_SESSION(true);
      const token = await window.GET_ACCOUNT_TOKEN(session, newAccountId, false);
      if (isNullish(token))
        throw new Error(`Did not fetch valid account token for ${newAccountId}, failing gracefully...`);
      publishAuthAccountIdChanged(newAccountId);
    } catch (error) {
      publishErrorNotification({ message: ERRORS.switchAccounts }, error);
      try {
        // If it fails, attempt to rollback
        console.log('attempting to rollback user account change'); // eslint-disable-line
        const session = await window.GET_SESSION();
        const token = await window.GET_ACCOUNT_TOKEN(session, oldAccountId);
        if (isNullish(token))
          throw new Error(`Did not fetch valid account token for ${oldAccountId} either, logging out...`);
        publishAuthAccountIdChanged(oldAccountId);
        dispatch(switchAccount(oldAccountId));
      } catch (secondError) {
        console.error(secondError);
        // If rolling back fails too, stay safe by forcing a logout
        window.LOGOUT();
      }
    } finally {
      dispatch(doneSwitchingAccount());
      toggleMenu();
    }
  };

  const toggleMenu = React.useCallback(() => {
    if (!state.switchingAccount && state.overlay !== OVERLAYS.menu) {
      dispatch(setOverlay(OVERLAYS.menu));
    } else {
      dispatch(setOverlay(null));
    }
  }, [dispatch, state.overlay, state.switchingAccount]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    publishTheme(newTheme);
  };

  const signOut = React.useCallback(() => {
    dispatch(setOverlay(null));
    window.LOGOUT();
  }, [dispatch]);

  const renderContentDesktop = () => {
    return (
      <div className={styles.bar}>
        <div
          className={classnames([
            styles.barContents,
            {
              [styles.barContentsLandingPage]: props.isLandingPage,
              [styles.barContentsTradingPage]: props.isTradingPage
            }
          ])}>
          <HorizontalNavigation isTransparent={props.isTransparent} isTradingPage={props.isTradingPage} />
          <HeaderMenuDesktop
            switchAccount={doSwitchAccount}
            toggleTheme={toggleTheme}
            toggleMenu={toggleMenu}
            signOut={signOut}
          />
          {state.switchingAccount && <div className={styles.overlay} />}
        </div>
      </div>
    );
  };

  const renderContentMobile = () => {
    const Container = props.isLandingPage || window.PLATFORM !== 'web' ? StickyHeader : React.Fragment;
    return (
      <Container>
        <div className={styles.bar}>
          <div className={styles.barContents}>
            <HeaderMenuMobile
              switchAccount={doSwitchAccount}
              toggleTheme={toggleTheme}
              toggleMenu={toggleMenu}
              signOut={signOut}
            />
          </div>
        </div>
      </Container>
    );
  };

  return !isMobile ? renderContentDesktop() : renderContentMobile();
}

Header.propTypes = {
  isLandingPage: PropTypes.bool.isRequired, // eslint-disable-line
  isTradingPage: PropTypes.bool.isRequired,
  isTransparent: PropTypes.bool.isRequired // eslint-disable-line
};

export default Header;
